import React, { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import themeable from 'zignal-landscape/lib/themeable';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import { Dark } from 'themes';
import noop from 'lodash/noop';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import classnames from 'classnames';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from 'react-query';
import apiClient from '../../helpers/apiClient';
import ToolTip from 'zignal-landscape/lib/ToolTip';
import MfeSearchDropDown from '../../components/MfeSearchDropDown/MfeSearchDropDown';
import DashboardSummary, { SummaryButton } from '../DashboardSummary';
// icons
import DropdownIcon from 'zignal-landscape/lib/Icon/DropdownIcon';
import Remove3Icon from 'zignal-landscape/lib/Icon/Remove3Icon';
import Add1Icon from 'zignal-landscape/lib/Icon/Add1Icon';
import LinkIcon from 'zignal-landscape/lib/Icon/LinkIcon';
import FullscreenIcon from 'zignal-landscape/lib/Icon/FullscreenIcon';
import ReportIcon from 'zignal-landscape/lib/Icon/ReportIcon';
import Remove1Icon from 'zignal-landscape/lib/Icon/Remove1Icon';
import MfeDashMySharedDashIcon from 'zignal-landscape/lib/Icon/MfeDashMySharedDashIcon';
import MfeDashSharedByOthersIcon from 'zignal-landscape/lib/Icon/MfeDashSharedByOthersIcon';
import MfeDashFav2Icon from 'zignal-landscape/lib/Icon/MfeDashFav2Icon';
import MfeDashUnFavIcon from 'zignal-landscape/lib/Icon/MfeDashUnFavIcon';
import MfeNavUserIcon from 'zignal-landscape/lib/Icon/MfeNavUserIcon';
import MfeDashEditIcon from 'zignal-landscape/lib/Icon/MfeDashEditIcon';
import MfeDashDuplicateIcon from 'zignal-landscape/lib/Icon/MfeDashDuplicateIcon';
import MfeDashDeleteIcon from 'zignal-landscape/lib/Icon/MfeDashDeleteIcon';
import MfeSidebarSettingsIcon from 'zignal-landscape/lib/Icon/MfeSidebarSettingsIcon';
import MfeAddProfileIcon from 'zignal-landscape/lib/Icon/MfeAddProfileIcon';
import MfeDashFilterIcon from 'zignal-landscape/lib/Icon/MfeDashFilterIcon';
import MfeDashFilterSelectedIcon from 'zignal-landscape/lib/Icon/MfeDashFilterSelectedIcon';
import MfeDashGotoIcon from 'zignal-landscape/lib/Icon/MfeDashGotoIcon';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } }
});

// action menu for model, profiles, and filters. actions are hidden by ellipsis until user hovers
const ActionMenu = ({styles, isVisible=true, actions=[], itemId}) => {
  return !isVisible ? null : (
    <div className={styles.DashboardPageHeaderModelActionMenu}>
      <div className={styles.DashboardPageHeaderModelActionsHidden}>...</div>
      <div className={styles.DashboardPageHeaderModelActionsRevealed}>
        {actions.map(({testId, icon: Icon, tooltipText, disabled, onClick}, i) => {
          const icon = (<Icon
            data-cy={testId} key={i}
            className={classnames(styles.DashboardPageHeaderIcon, disabled && styles.DashboardPageHeaderIconDisabled)}
            onClick={disabled ? noop : (e) => onClick(e, itemId)}
          />);
          return disabled ? icon : <ToolTip key={i} tooltipText={tooltipText}>{icon}</ToolTip>;
        })}
      </div>
    </div>
  );};

// get tooltip text for shared by others models
const getSharedOwnerText = ({isShared, isOwner, model}) => {
  if (isShared && !isOwner && model.user) {
    if (model.ownerFirstName && model.ownerLastName) {
      return `Created by ${model.ownerFirstName} ${model.ownerLastName}`;
    }
    return 'User No Longer Exists';
  }
};

// determine the icons shown next to the model title
const getModelIcon = ({isOwner, isShared, isDefault}) => {
  if (isDefault) {
    return MfeDashSharedByOthersIcon;
  } else if (isShared) {
    return isOwner ? MfeDashMySharedDashIcon : MfeDashSharedByOthersIcon;
  }
  return MfeNavUserIcon;
};

// widget dropdown options
const getWidgetOptions = ({bulkEditWidgets, closeAllWidgetsConfirm}) => {
  return compact([
    { title: 'Bulk Edit', icon: MfeDashEditIcon, onItemClick: bulkEditWidgets, newIcon: true },
    { title: 'Remove All Widgets', icon: Remove3Icon, onItemClick: closeAllWidgetsConfirm }
  ]);
};

// settings dropdown options
const getSettingsOptions = ({
  modelTypeName,
  isOwner,
  isShared,
  isFavorite,
  isDashboard,
  isDefault,
  showFullscreen,
  isCampaignAdmin,
  isDashboardAdmin,
  //actions
  newModel,
  editModel,
  duplicateModel,
  destroyModel,
  createReport,
  createDashboardLink,
  toggleShared,
  toggleFavorite,
  enterFullScreen,
  exitFullScreen
}) => {
  const EDIT = { title: `Edit ${modelTypeName}`, icon: MfeDashEditIcon, onItemClick: editModel, newIcon: true };
  const DELETE = { title: `Delete ${modelTypeName}`, icon: MfeDashDeleteIcon, onItemClick: destroyModel, newIcon: true };
  const NEW = { title: `New ${modelTypeName}`, icon: Add1Icon, onItemClick: newModel};
  const DUPLICATE = { title: `Duplicate ${modelTypeName}`, icon: MfeDashDuplicateIcon, onItemClick: duplicateModel, newIcon: true };
  const REPORT = { title: 'Create New Report', icon: ReportIcon, onItemClick: createReport };
  const LINK = { title: 'Create New Dashboard Link', icon: LinkIcon, onItemClick: createDashboardLink, };
  const SHARE = { title: `${isShared ? 'Unshare' : 'Share'} with Workspace`, icon: MfeDashMySharedDashIcon, onItemClick: toggleShared };
  const FAVORITE = {
    title: `${isFavorite ? 'Unset' : 'Set'} as Favorite`,
    icon: isFavorite ? MfeDashUnFavIcon  : MfeDashFav2Icon, onItemClick: toggleFavorite, newIcon: true
  };
  const isInFullscreen = window.Util.inFullscreen;
  const FULLSCREEN = {
    title: `${isInFullscreen ? 'Exit' : 'Go'} Fullscreen`, icon: FullscreenIcon,
    onItemClick: isInFullscreen ? exitFullScreen : enterFullScreen
  };

  return compact([
    !isDefault && isOwner && EDIT,
    !isDefault && isOwner && DELETE,
    NEW,
    DUPLICATE,
    isDashboard && REPORT,
    !isDefault && isDashboard && LINK,
    !isDefault && isDashboardAdmin && SHARE,
    !isDefault && isCampaignAdmin && FAVORITE,
    isDashboard && showFullscreen && FULLSCREEN
  ]);
};

const DashboardPageHeader = (props) => {
  const mergedProps = {
    styles: {},
    model: {},
    profiles: [],
    filterModels: [],
    selectedProfileId: '',
    campaignId: '',
    filterId: '',
    modelTypeName: '',
    isShared: false,
    isFavorite: false,
    isDashboard: false,
    showFullscreen: false,
    isOwner: false,
    isCampaignAdmin: false,
    isDashboardAdmin: false,
    isDashboardUser: false,
    isDefault: false,
    account: null,
    openSelectionModal: noop,
    bulkEditWidgets: noop,
    closeAllWidgetsConfirm: noop,
    handleCampaignChange: noop,
    addWidget: noop,
    changeProfile: noop,
    changeFilter: noop,
    addFilter: noop,
    duplicateFilter: noop,
    editFilter: noop,
    goToProfile: noop,
    newModel: noop,
    editModel: noop,
    duplicateModel: noop,
    destroyModel: noop,
    createReport: noop,
    createDashboardLink: noop,
    toggleShared: noop,
    toggleFavorite: noop,
    enterFullScreen: noop,
    exitFullScreen: noop,
    ...props
  };
  const {
    styles,
    model,
    profiles,
    filterModels,
    selectedProfileId,
    campaignId,
    filterId,
    isOwner,
    isDashboardAdmin,
    isDashboardUser,
    isDefault,
    isDashboard,
    account,
    // actions
    openSelectionModal,
    editModel,
    duplicateModel,
    destroyModel,
    addWidget,
    changeProfile,
    changeFilter,
    addFilter,
    duplicateFilter,
    editFilter,
    goToProfile
  } = mergedProps;

  const queryClientInner = useQueryClient();

  const { data: filtersData, isError: filtersError } = useQuery({
    queryKey: ['smart_filters', campaignId],
    queryFn: () => apiClient(`/api/campaigns/${campaignId}/smart_filters`),
    retry: (failureCount, error) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) { return false; }
      return failureCount < 3;
    },
  });

  const hasFetchedFilters = !!filtersData;
  const {
    canCreateFilter=false, // false if SF limit has been reached
    filters=[]
  } = filtersData || {};

  // while loading, we can use the backbone models
  const smartFilters = sortBy(hasFetchedFilters ? filters : filterModels, 'name');

  useEffect(() => {
    if (filterId && hasFetchedFilters && !filtersError && !filters.find(({id}) => id === filterId)) {
      queryClientInner.invalidateQueries(['smart_filters', campaignId]);
    }
  }, [filterId, hasFetchedFilters, filtersError, filters, campaignId, queryClientInner]);

  // SmartFilter mutations are owned by the Backbone layer; it announces success
  // via Vent('change:dashboard'). Bridge that into react-query to refresh chip+tooltip data.
  useEffect(() => {
    const handler = () => queryClientInner.invalidateQueries(['smart_filters', campaignId]);
    Vent.on('change:dashboard', handler);
    return () => Vent.off('change:dashboard', handler);
  }, [campaignId, queryClientInner]);

  // profiles
  const profileItems = profiles.map(({id, name: title}) => ({id, title}));
  let activeProfileId = selectedProfileId || profileItems[0] && profileItems[0].id;
  let activeProfile = profiles.find(({id}) => id === activeProfileId);
  // if we can't find the active profile (may have been deleted), then use first profile
  if (!activeProfile) {
    activeProfile = profiles[0] || {};
    activeProfileId = activeProfile.id;
  }

  // smartfilters
  const activeFilter = smartFilters.find(({id}) => id === filterId) || {};
  const hasActiveFilter = !!activeFilter.id;
  const showFilter = !isDefault;
  const showCreateFilterAction = canCreateFilter;
  const showDuplicateFilterAction = hasActiveFilter && showCreateFilterAction;

  // classNames
  const modelContainerClassNames = { [styles.DashboardPageHeaderModelContainer]: true, [styles.readOnly]: isDashboardUser };
  const widgetContainerClassNames = { [styles.DashboardPageHeaderAddWidgetContainer]: true, [styles.disabled]: !isOwner };
  const smartFilterButtonContainerClassNames = {
    [styles.DashboardPageHeaderButtonContainer]: true
  };
  const smartFilterButtonClassNames = {
    [styles.DashboardPageHeaderButton]: true,
    [styles.DashboardPageHeaderButtonSmartFilter]: true,
    [styles.selected]: hasActiveFilter,
    [styles.disabled]: !isOwner
  };
  const datepickerClassNames = {
    // using global className here, this is still used by backbone by Views.DateRangePicker
    'daterangepicker-wrapper': true,
    [styles.DashboardPageHeaderDatePicker]: true
  };

  const sharedOwnerText = getSharedOwnerText(mergedProps);
  const ModelIcon = getModelIcon(mergedProps);
  const settings = getSettingsOptions(mergedProps);
  const widgetOptions = getWidgetOptions(mergedProps);

  const onRemoveFilter = useCallback((e) => {
    e.stopPropagation();
    changeFilter(null);
  }, [changeFilter]);

  const onGoToProfile = useCallback(() => {
    goToProfile(activeProfileId);
  }, [goToProfile, activeProfileId]);

  return (<ThemeProvider theme={Dark}>
    <div className={styles.DashboardPageHeader}>
      <div className={styles.DashboardPageHeaderTopRow}>
        <div className={classnames(modelContainerClassNames)} onClick={isDashboardUser ? null : openSelectionModal}>
          <div className={styles.DashboardPageHeaderModelIcon}>
            { sharedOwnerText ? (
              <ToolTip tooltipText={sharedOwnerText} position="bottom">
                <ModelIcon data-cy="dph-model-icon" className={styles.DashboardPageHeaderXLIcon} />
              </ToolTip>
            ) : (
              <ModelIcon data-cy="dph-model-icon" className={styles.DashboardPageHeaderXLIcon} />
            )}
          </div>
          <div className={styles.DashboardPageHeaderModelTitle}>{model.title}</div>
          { isDashboardUser ? null : (
            <div className={styles.DashboardPageHeaderModelIcon}><DropdownIcon className={styles.DashboardPageHeaderSmallIcon} /></div>
          )}
        </div>
        <ActionMenu
          styles={styles}
          isVisible={!isDefault && !isDashboardUser}
          actions={compact([
            isOwner && {icon: MfeDashEditIcon, tooltipText: 'Edit', onClick: editModel },
            { testId: 'dph-model-dup', tooltipText: 'Duplicate', icon: MfeDashDuplicateIcon, onClick: duplicateModel },
            isOwner && { icon: MfeDashDeleteIcon, tooltipText: 'Delete', onClick: destroyModel }
          ])}
        />
        { isDashboardUser ? null : (
          <div className={styles.DashboardPageHeaderModelButtons}>
            <div className={classnames(widgetContainerClassNames)}>
              {isOwner ? (
                <ToolTip tooltipText="Add Widget">
                  <div
                    data-cy="dph-add-widget"
                    className={styles.DashboardPageHeaderAddWidgetButton}
                    onClick={addWidget}
                  >+</div>
                </ToolTip>
              ) : (
                <div
                  data-cy="dph-add-widget"
                  className={styles.DashboardPageHeaderAddWidgetButton}
                >+</div>
              )}
              <MfeSearchDropDown
                testId="dph-widget-options"
                className={styles.DashboardPageHeaderAddWidgetChevron}
                items={widgetOptions}
                isSmall
                disabled={!isOwner}
              >
                <DropdownIcon className={styles.DashboardPageHeaderXSIcon} />
              </MfeSearchDropDown>
            </div>
            <MfeSearchDropDown
              testId="dph-settings"
              className={styles.DashboardPageHeaderSettingsButton}
              classNameActive={styles.DashboardPageHeaderSettingsButtonActive}
              items={settings}
              isSmall
            >
              <MfeSidebarSettingsIcon className={styles.DashboardPageHeaderLargeIcon} />
            </MfeSearchDropDown>
          </div>
        )}
      </div>
      <div className={styles.DashboardPageHeaderBottomRow}>
        <div className={styles.DashboardPageHeaderButtonContainer}>
          <MfeSearchDropDown
            testId="dph-profiles"
            placeholder="Dataset..."
            className={styles.DashboardPageHeaderButton}
            classNameActive={styles.DashboardPageHeaderButtonActive}
            items={profileItems}
            activeItemId={activeProfileId}
            onItemsClick={changeProfile}
            positionLeft
            isSearchable
          >
            <MfeAddProfileIcon className={styles.DashboardPageHeaderIcon} />
            <div className={styles.DashboardPageHeaderButtonText}>{ activeProfile.name }</div>
            <DropdownIcon className={styles.DashboardPageHeaderXSIcon} />
          </MfeSearchDropDown>
          <ActionMenu
            isVisible={!isDashboardUser}
            styles={styles}
            actions={[
              { testId: 'dph-gotoprofile', icon: MfeDashGotoIcon, tooltipText: 'View', onClick: onGoToProfile }
            ]}
          />
          {showFilter && <div className={styles.DashboardPageHeaderButtonContainerDivider} /> }
        </div>
        <div className={classnames(smartFilterButtonContainerClassNames)}>
          {showFilter && <>
            <MfeSearchDropDown
              testId="dph-smartfilters"
              placeholder="Smart Filter..."
              className={classnames(smartFilterButtonClassNames)}
              classNameActive={hasActiveFilter ? '' : styles.DashboardPageHeaderButtonActive}
              items={smartFilters}
              activeItem={activeFilter}
              activeItemId={filterId}
              onItemsClick={changeFilter}
              positionLeft
              isSearchable
              disabled={!isOwner}
            >
              { hasActiveFilter ? (
                <>
                  <MfeDashFilterSelectedIcon className={styles.DashboardPageHeaderSFIcon} />
                  <div className={styles.DashboardPageHeaderButtonTextContainer}>
                    <div className={styles.DashboardPageHeaderButtonText}>{  activeFilter.name }</div>
                    { isOwner &&
                      <>
                        <Remove1Icon className={styles.DashboardPageHeaderSmallIcon} />
                        <div className={styles.DashboardPageHeaderRemoveFilter} onMouseDown={onRemoveFilter} />
                      </>
                    }
                  </div>
                </>
              ) : (
                <>
                  <MfeDashFilterIcon className={styles.DashboardPageHeaderIcon} />
                  <div className={styles.DashboardPageHeaderButtonText}>Smart Filter</div>
                  <DropdownIcon className={styles.DashboardPageHeaderXSIcon} />
                </>
              )}
            </MfeSearchDropDown>
            <ActionMenu
              isVisible={isOwner && isDashboardAdmin && !isDashboardUser}
              styles={styles}
              itemId={activeFilter.id}
              actions={compact([
                hasActiveFilter && { icon: MfeDashEditIcon, tooltipText: 'Edit', onClick: editFilter },
                hasActiveFilter && { icon: MfeDashDuplicateIcon, tooltipText: 'Duplicate', onClick: duplicateFilter, disabled: !showDuplicateFilterAction },
                { testId: 'dph-addfilter', icon: Add1Icon, onClick: addFilter, tooltipText: 'Add', disabled: !showCreateFilterAction }
              ])}
            />
          </>
          }
          <div className={styles.DashboardPageHeaderButtonContainerDivider} />
        </div>

        {isDashboard && (
          <div className={styles.DashboardPageHeaderButtonContainer}>
            <SummaryButton dashboardId={model.id || model.candidate} activeFilter={activeFilter} activeProfileId={activeProfileId} />
            <div className={styles.DashboardPageHeaderButtonContainerDivider} />
          </div>
        )}

        <div className={styles.DashboardPageHeaderButtonContainer}>
          <div className={classnames(datepickerClassNames)}>
            <div id="datepickerlandscape" />
          </div>
        </div>
      </div>

      {isDashboard && (
        <>
          <DashboardSummary
            dashboardId={model.id || model.candidate}
            account={account}
            activeFilter={activeFilter}
            activeProfileId={activeProfileId}
          />
        </>
      )}
    </div>
  </ThemeProvider>);
};

DashboardPageHeader.propTypes = {
  styles: PropTypes.object,
  model: PropTypes.object,
  profiles: PropTypes.array,
  filterModels: PropTypes.array,
  modelTypeName: PropTypes.string,
  selectedProfileId: PropTypes.string,
  campaignId: PropTypes.string,
  filterId: PropTypes.string,
  isOwner: PropTypes.bool,
  isCampaignAdmin: PropTypes.bool,
  isDashboardAdmin: PropTypes.bool,
  isDashboardUser: PropTypes.bool,
  isDefault: PropTypes.bool,
  isShared: PropTypes.bool,
  isFavorite: PropTypes.bool,
  isDashboard: PropTypes.bool,
  showFullscreen: PropTypes.bool,
  account: PropTypes.object,
  // actions
  openSelectionModal: PropTypes.func,
  bulkEditWidgets: PropTypes.func,
  closeAllWidgetsConfirm: PropTypes.func,
  addWidget: PropTypes.func,
  changeProfile: PropTypes.func,
  changeFilter: PropTypes.func,
  addFilter: PropTypes.func,
  duplicateFilter: PropTypes.func,
  editFilter: PropTypes.func,
  goToProfile: PropTypes.func,
  newModel: PropTypes.func,
  editModel: PropTypes.func,
  duplicateModel: PropTypes.func,
  destroyModel: PropTypes.func,
  createReport: PropTypes.func,
  createDashboardLink: PropTypes.func,
  toggleShared: PropTypes.func,
  toggleFavorite: PropTypes.func,
  enterFullScreen: PropTypes.func,
  exitFullScreen: PropTypes.func
};

const DashboardPageHeaderInner = themeable(DashboardPageHeader);

const DashboardPageHeaderQueryWrapper = (props) => {
  return (<QueryClientProvider client={queryClient}>
    <DashboardPageHeaderInner {...props} />
  </QueryClientProvider>);
};

export { queryClient };
export default DashboardPageHeaderQueryWrapper;
